import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  mergeMap,
  map,
  catchError,
  of,
  switchMap,
  withLatestFrom,
  filter,
  tap,
} from 'rxjs';
import * as ruleActions from '../actions/rules.actions';
import * as appActions from '../common/app.action';
import { RuleService } from '../../services/rule.service';
import { select, Store } from '@ngrx/store';
import { selectRules, selectRulesEntities } from '../selectors/rules.selector';
import { selectFilters } from '../selectors/rules.selector';
import { selectTableState } from '../common/app.selectors';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { EvaluationService } from '../../services/evaluation.service';
import { mapErrorMessage } from '../../../utils/error.utils';
import { ERROR_KEYS } from '../../shared/constants/error-keys.constant';

@Injectable()
export class RulesEffects {
  constructor(
    private actions$: Actions,
    private ruleService: RuleService,
    private store: Store,
    private messageService: MessageService,
    private evaluationService: EvaluationService,
  ) {}

  loadRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ruleActions.loadRule),
      switchMap((action) => {
        return this.ruleService.GetByUid(action.uniqueId).pipe(
          map((rule) => {
            return ruleActions.loadRuleSuccess({ rule: rule });
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404)
              this.store.dispatch(appActions.loadNotFound());
            return of(ruleActions.loadRulesFailure({ error }));
          }),
        );
      }),
    ),
  );

  createRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ruleActions.createRule),
      mergeMap(({ createRuleRequest }) => {
        return this.ruleService.CreateRule(createRuleRequest).pipe(
          map((rule) => ruleActions.createRuleSuccess({ rule })),
          catchError((error) => {
            let message = 'Échec de la création de la règle';
            try {
              const parsed = JSON.parse(error?.body ?? '{}');
              message = parsed?.message || message;
            } catch {
              message = error?.message || message;
            }
            const finalMessage = message.includes(
              ERROR_KEYS.PYTHON_EXPRESSION_TEST_FAILED,
            )
              ? message
              : mapErrorMessage(message);
            return of(
              ruleActions.createRuleFailure({
                error,
                message: finalMessage,
              }),
            );
          }),
        );
      }),
    ),
  );

  updateRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ruleActions.updateRule),
      mergeMap(({ updateRuleRequest }) => {
        return this.ruleService.UpdateRule(updateRuleRequest).pipe(
          map((rule) => ruleActions.updateRuleSuccess({ rule })),
          catchError((error) => {
            let message = 'Échec de la modification de la règle';
            try {
              const parsed = JSON.parse(error?.body ?? '{}');
              message = parsed?.message || message;
            } catch {
              message = error?.message || message;
            }
            const finalMessage = message.includes(
              ERROR_KEYS.PYTHON_EXPRESSION_TEST_FAILED,
            )
              ? message
              : mapErrorMessage(message);
            return of(
              ruleActions.updateRuleFailure({
                error,
                message: finalMessage,
              }),
            );
          }),
        );
      }),
    ),
  );

  dropRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ruleActions.dropRule),
      mergeMap(({ uid }) => {
        return this.ruleService.DropRule(uid).pipe(
          map(() => ruleActions.dropRuleSuccess({ uid })),
          catchError((error) => {
            return of(ruleActions.dropRuleFailure({ error }));
          }),
        );
      }),
    ),
  );

  createRuleSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ruleActions.createRuleSuccess),
        tap(() => {
          this.messageService.add({
            key: 'globalToast',
            severity: 'success',
            summary: 'Succès',
            detail: 'La règle a été créée avec succès',
          });
        }),
      ),
    { dispatch: false },
  );

  createRuleFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ruleActions.createRuleFailure),
        tap(({ error }) => {
          const parsedError = JSON.parse(error?.body || '{}');
          this.messageService.add({
            key: 'globalToast',
            severity: 'error',
            summary: 'Erreur',
            detail: parsedError?.message ?? 'Échec de la création de la règle',
          });
        }),
      ),
    { dispatch: false },
  );

  updateRuleEvaluation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ruleActions.updateRuleEvaluation),
      mergeMap(({ ruleUid, updateEvaluationRequest }) => {
        return this.evaluationService
          .UpdateEvaluation(updateEvaluationRequest)
          .pipe(
            map((evaluation) =>
              ruleActions.updateRuleEvaluationSuccess({ ruleUid, evaluation }),
            ),
            catchError((error) => {
              return of(ruleActions.updateRuleEvaluationFailure({ error }));
            }),
          );
      }),
    ),
  );

  createRuleEvaluation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ruleActions.createRuleEvaluation),
      mergeMap(({ ruleUid, createEvaluationRequest }) => {
        return this.evaluationService
          .CreateEvaluation(createEvaluationRequest)
          .pipe(
            map((evaluation) =>
              ruleActions.updateRuleEvaluationSuccess({ ruleUid, evaluation }),
            ),
            catchError((error) => {
              return of(ruleActions.updateRuleEvaluationFailure({ error }));
            }),
          );
      }),
    ),
  );

  updateRuleEvaluationVigie$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ruleActions.updateRuleEvaluationVigie),
      mergeMap(
        ({ ruleUid, evaluationUid, potentialCause, correctiveAction }) => {
          return this.evaluationService
            .UpdateEvaluationVigie(evaluationUid, {
              potentialCause,
              correctiveAction,
            })
            .pipe(
              map((evaluation) =>
                ruleActions.updateRuleEvaluationSuccess({
                  ruleUid,
                  evaluation,
                }),
              ),
              catchError((error) =>
                of(ruleActions.updateRuleEvaluationVigieFailure({ error })),
              ),
            );
        },
      ),
    ),
  );

  // Server-side paged load
  loadRulesPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ruleActions.loadRulesPage),
      tap((action) => {
        if (action.needToReload) {
          this.store.dispatch(ruleActions.clearPagedRules());
        }
      }),
      switchMap((action) => {
        const payload = {
          First: action.first ?? 0,
          Rows: action.rows ?? 10,
          SortField: action.sortField ?? null,
          SortOrder: action.sortOrder ?? 0,
          Filters: action.filters ?? {},
          GlobalFilter: action.globalFilter ?? null,
          Factory: action.factory ?? '',
          IsOrderedByHealthIndicator: action.showHealthIndicator ?? false,
        };

        return this.ruleService.GetPaged(payload).pipe(
          map((resp) => {
            const rules = resp.items ?? [];
            return ruleActions.loadRulesPageSuccess({
              rules: rules,
              totalRecords: resp?.totalRecords ?? 0,
            });
          }),
          catchError((error: HttpErrorResponse) => {
            console.log('error : ', error);
            return of(ruleActions.loadRulesPageFailure({ error }));
          }),
        );
      }),
    ),
  );

  loadRuleStatistics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ruleActions.loadRuleStatistics),
      switchMap(() =>
        this.ruleService.GetRuleStats().pipe(
          map((statistics) =>
            ruleActions.loadRuleStatisticsSuccess({ statistics }),
          ),
          catchError((error) =>
            of(ruleActions.loadRuleStatisticsFailure({ error })),
          ),
        ),
      ),
    ),
  );

  loadAlarmStatistics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ruleActions.loadAlarmStatistics),
      switchMap(() =>
        this.ruleService.GetAlarmStats().pipe(
          map((alarmStatistics) =>
            ruleActions.loadAlarmStatisticsSuccess({ alarmStatistics }),
          ),
          catchError((error) =>
            of(ruleActions.loadAlarmStatisticsFailure({ error })),
          ),
        ),
      ),
    ),
  );
}
